/**
 * Browser client for a customer account hosted outside Shoppex.
 *
 * The publishable key identifies the merchant and the dashboard origin
 * allowlist authorizes the browser origin. OTP verification returns a separate
 * short-lived `hcs_` token; the SDK never exposes Shoppex's BetterAuth token.
 */
import {
  customerLoyaltyRedeemSchema,
  customerLoyaltySchema,
  customerPortalAffiliateSummarySchema,
  customerPortalAffiliateStatsSchema,
  customerPortalAffiliateCreateLinkSchema,
  customerPortalAvatarSchema,
  customerPortalDashboardSchema,
  customerPortalEmailPreferencesSchema,
  customerPortalEmailPreferencesUpdateSchema,
  customerPortalFavoriteMutationSchema,
  customerPortalFavoritesSchema,
  customerPortalInvoiceDetailSchema,
  customerPortalLicenseResetSchema,
  customerPortalLicenseRenewalSchema,
  customerPortalMeSchema,
  customerPortalNotificationPreferencesSchema,
  customerPortalOrderReviewSchema,
  customerPortalPaginatedInvoicesSchema,
  customerPortalProfileUpdateSchema,
  customerPortalSubscriptionBillingHistorySchema,
  customerPortalSubscriptionCancelSchema,
  customerPortalSubscriptionStatusSchema,
  customerPortalSupportCreateSchema,
  customerPortalSupportTicketDetailSchema,
  customerPortalWalletTopupQuoteSchema,
  customerPortalWalletTopupResultSchema,
  customerReplacementEligibilitySchema,
  customerReplacementRequestSchema,
  customerResellerApiKeysSchema,
  customerResellerCatalogSchema,
  customerResellerEnrollmentSchema,
  customerResellerOrderSchema,
  customerResellerOrdersSchema,
  customerResellerQuoteSchema,
  customerResellerSummarySchema,
  customerResellerTopupSchema,
  customerResellerWalletSchema,
  customerWarrantyClaimSchema,
  customerWarrantyListSchema,
  type CustomerLoyaltyRedeemWire,
  type CustomerLoyaltyWire,
  type CustomerPortalAffiliateSummaryWire,
  type CustomerPortalAffiliateStatsWire,
  type CustomerPortalAffiliateCreateLinkWire,
  type CustomerPortalAvatarWire,
  type CustomerPortalDashboardWire,
  type CustomerPortalEmailPreferencesUpdateWire,
  type CustomerPortalEmailPreferencesWire,
  type CustomerPortalFavoriteMutationWire,
  type CustomerPortalFavoritesWire,
  type CustomerPortalLicenseResetWire,
  type CustomerPortalLicenseRenewalWire,
  type CustomerPortalNotificationPreferencesWire,
  type CustomerPortalOrderReviewWire,
  type CustomerPortalProfileUpdateWire,
  type CustomerPortalSubscriptionBillingHistoryWire,
  type CustomerPortalSubscriptionCancelWire,
  type CustomerPortalSubscriptionStatusWire,
  type CustomerPortalSupportCreateWire,
  type CustomerPortalSupportTicketDetailWire,
  type CustomerPortalWalletTopupQuoteWire,
  type CustomerPortalWalletTopupResultWire,
  type CustomerReplacementEligibilityWire,
  type CustomerReplacementRequestWire,
  type CustomerResellerApiKeysWire,
  type CustomerResellerCatalogWire,
  type CustomerResellerEnrollmentWire,
  type CustomerResellerOrderWire,
  type CustomerResellerOrdersWire,
  type CustomerResellerQuoteWire,
  type CustomerResellerSummaryWire,
  type CustomerResellerTopupWire,
  type CustomerResellerWalletWire,
  type CustomerWarrantyClaimWire,
  type CustomerWarrantyListWire,
} from '@shoppex/contracts/customer-portal-wire';

const DEFAULT_API_ORIGIN = 'https://api.shoppex.io';
const SESSION_STORAGE_PREFIX = 'shoppex_headless_customer_session:';

interface CustomerPayloadSchema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false };
}

interface ApiEnvelope<T> {
  status: number;
  data: T | null;
  error: string | null;
  message?: string | null;
  error_code?: string | null;
}

interface StoredSession {
  accessToken: string;
  expiresAt: string;
  savedAt: number;
}

export interface HeadlessCustomerClientConfig {
  /** Shop slug or Shoppex storefront domain. */
  shop: string;
  /** Existing Headless Checkout publishable key (`pk_live_` / `pk_test_`). */
  publishableKey: string;
  /** API origin override. Defaults to https://api.shoppex.io. */
  apiBaseUrl?: string;
  /** Optional fetch override for tests or compatible runtimes. */
  fetchImpl?: typeof fetch;
  /** Optional session storage override. Defaults to browser sessionStorage. */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Optional persistent storage override. Defaults to browser localStorage. */
  persistentStorage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

export interface HeadlessCustomerSession {
  accessToken: string;
  expiresAt: string;
}

export interface HeadlessCustomerVerifyOptions {
  /** Persist the customer session across browser restarts for up to 30 days. */
  rememberMe?: boolean;
}

export interface HeadlessCustomerOrdersQuery {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export interface HeadlessCustomerDownload {
  blob: Blob;
  filename: string | null;
  contentType: string | null;
}

type HeadlessCustomerDashboardDeliverable =
  CustomerPortalDashboardWire['deliverables'][number] & {
    /** Pass this value with `invoice_uniqid` to `customer.download()`. */
    downloadIndex?: number | null;
  };

export type HeadlessCustomerDashboard = Omit<CustomerPortalDashboardWire, 'deliverables'> & {
  deliverables: HeadlessCustomerDashboardDeliverable[];
};
export type HeadlessCustomerProfileUpdate = CustomerPortalProfileUpdateWire;
export type HeadlessCustomerAvatar = CustomerPortalAvatarWire;
export type HeadlessCustomerEmailPreferences = CustomerPortalEmailPreferencesWire;
export type HeadlessCustomerEmailPreferencesUpdate = CustomerPortalEmailPreferencesUpdateWire;
export type HeadlessCustomerNotificationPreferences = CustomerPortalNotificationPreferencesWire;
export type HeadlessCustomerOrderReview = CustomerPortalOrderReviewWire;
export type HeadlessCustomerFavorites = CustomerPortalFavoritesWire;
export type HeadlessCustomerFavoriteMutation = CustomerPortalFavoriteMutationWire;
export type HeadlessCustomerAffiliate = CustomerPortalAffiliateSummaryWire;
export type HeadlessCustomerAffiliateStats = CustomerPortalAffiliateStatsWire;
export type HeadlessCustomerAffiliateLink = CustomerPortalAffiliateCreateLinkWire;
export type HeadlessCustomerTicket = CustomerPortalSupportTicketDetailWire;
export type HeadlessCustomerTicketCreateResult = CustomerPortalSupportCreateWire;
export type HeadlessCustomerLicenseReset = CustomerPortalLicenseResetWire;
export type HeadlessCustomerLicenseRenewal = CustomerPortalLicenseRenewalWire;
export type HeadlessCustomerWalletTopupQuote = CustomerPortalWalletTopupQuoteWire;
export type HeadlessCustomerWalletTopup = CustomerPortalWalletTopupResultWire;
export type HeadlessCustomerSubscriptionBillingHistory = CustomerPortalSubscriptionBillingHistoryWire;
export type HeadlessCustomerSubscriptionCancel = CustomerPortalSubscriptionCancelWire;
export type HeadlessCustomerSubscriptionStatus = CustomerPortalSubscriptionStatusWire;
export type HeadlessCustomerLoyalty = CustomerLoyaltyWire;
export type HeadlessCustomerLoyaltyRedeem = CustomerLoyaltyRedeemWire;
export type HeadlessCustomerWarranties = CustomerWarrantyListWire;
export type HeadlessCustomerWarrantyClaim = CustomerWarrantyClaimWire;
export type HeadlessCustomerReplacementEligibility = CustomerReplacementEligibilityWire;
export type HeadlessCustomerReplacementRequest = CustomerReplacementRequestWire;
export type HeadlessCustomerReseller = CustomerResellerSummaryWire;
export type HeadlessCustomerResellerEnrollment = CustomerResellerEnrollmentWire;
export type HeadlessCustomerResellerCatalog = CustomerResellerCatalogWire;
export type HeadlessCustomerResellerQuote = CustomerResellerQuoteWire;
export type HeadlessCustomerResellerOrders = CustomerResellerOrdersWire;
export type HeadlessCustomerResellerOrder = CustomerResellerOrderWire;
export type HeadlessCustomerResellerWallet = CustomerResellerWalletWire;
export type HeadlessCustomerResellerApiKeys = CustomerResellerApiKeysWire;
export type HeadlessCustomerResellerTopup = CustomerResellerTopupWire;

export interface HeadlessCustomerTicketInput {
  title?: string;
  message: string;
  invoiceId?: string;
}

export interface HeadlessCustomerOrderReviewInput {
  score: 1 | 3 | 5;
  message: string;
}

export interface HeadlessCustomerAffiliateLinkInput {
  code: string;
  label: string;
  requestedRevenuePercent?: number | null;
  requestedCustomerDiscountPercent?: number | null;
}

export interface HeadlessCustomerSubscriptionCancelOptions {
  cancelAtPeriodEnd?: boolean;
  reason?: string | null;
}

export interface HeadlessCustomerEmailPreferencesPatch {
  globalUnsubscribed?: boolean;
  listSubscriptions?: Array<{ listId: string; subscribed: boolean }>;
}

export interface HeadlessCustomerNotificationPreferencesPatch {
  orderCreatedEmails?: boolean;
  orderCompletedEmails?: boolean;
  paymentIssueEmails?: boolean;
}

export interface HeadlessCustomerReplacementOptions {
  lineItemId: string;
  reason: string;
  idempotencyKey?: string;
}

export interface HeadlessCustomerResellerCatalogQuery {
  search?: string;
  page?: number;
  perPage?: number;
}

export interface HeadlessCustomerResellerOrdersQuery {
  page?: number;
  perPage?: number;
}

export interface HeadlessCustomerResellerOrderItem {
  productId: string;
  variantId?: string;
  quantity: number;
}

/** Public DTOs mirror the producer-owned schemas imported above. Passing each
 * schema into `request<T>` makes TypeScript reject drift in required fields. */
export interface HeadlessCustomerMe {
  customer: {
    id: string | null;
    email: string;
    name: string;
    avatar_url: string | null;
    joined_at: string | null;
  };
  shop: {
    id: string;
    slug: string;
    name: string;
    currency: string;
    branding: {
      logo_url: string | null;
      primary_color: string;
      secondary_color: string;
      accent_color: string;
    };
  };
}

type HeadlessCustomerMeWire = HeadlessCustomerMe & { token: string | null };

export interface HeadlessCustomerOrderSummary {
  uniqid: string;
  status: string;
  total: string;
  totalDisplay: string | null;
  currency: string;
  createdAt: string;
  lineItems: Array<{
    productTitle: string | null;
    quantity: number;
    product: {
      attachment_imageId: { cloudflareImageId: string | null } | null;
    } | null;
  }>;
}

export interface HeadlessCustomerOrders {
  invoices: HeadlessCustomerOrderSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    has_more: boolean;
  };
}

export interface HeadlessCustomerPayment {
  gateway: string;
  displayName: string | null;
  cryptoGateway?: string | null;
  blockchain: string | null;
  cryptoAddress: string | null;
  apmMethod: string | null;
}

export interface HeadlessCustomerOrder {
  id: string;
  uniqid: string;
  status: string;
  statusDetails: string | null;
  total: string;
  totalDisplay: string | null;
  subtotal: string;
  discount: string;
  discountDisplay: string | null;
  tax: string;
  taxPercentage: string;
  currency: string;
  createdAt: string;
  updatedAt: string;
  gatewayUpdatedAt: string | null;
  customFields: Record<string, unknown> | null;
  balancePaidAmount: string;
  currentPaymentAttemptId: string | null;
  lineItems: Array<{
    id: string;
    productTitle: string;
    productType: string;
    variantTitle: string | null;
    unitPrice: string;
    quantity: number;
    total: string;
    deliveryText: string | null;
    deliverySummary: {
      codes: string[];
      links: Array<{
        label: string;
        value: string;
        /** External URL for non-Shoppex links. Portal-only download URLs are removed. */
        href?: string | null;
        /** Pass this value to `customer.download(orderId, downloadIndex)`. */
        downloadIndex?: number | null;
      }>;
      notes: string[];
      serials: Array<{ value: string; is_replacement: boolean; is_replaced: boolean }>;
    } | null;
    deliveredAt: string | null;
    deliveryStatus: 'PENDING' | 'AWAITING_FULFILLMENT' | 'DELIVERED' | 'FAILED';
    customFields: Record<string, unknown> | null;
    product: {
      uniqid: string;
      slug: string | null;
      serviceText: string | null;
      attachment_imageId: { cloudflareImageId: string | null } | null;
    } | null;
  }>;
  currentPayment: HeadlessCustomerPayment | null;
  payments: HeadlessCustomerPayment[];
}

export class HeadlessCustomerError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string | null,
  ) {
    super(message);
    this.name = 'HeadlessCustomerError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseStoredSession(value: string | null): StoredSession | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      !isRecord(parsed)
      || typeof parsed.accessToken !== 'string'
      || !parsed.accessToken.startsWith('hcs_')
      || typeof parsed.expiresAt !== 'string'
      || typeof parsed.savedAt !== 'number'
      || !Number.isFinite(parsed.savedAt)
    ) {
      return null;
    }
    const expiresAtMs = Date.parse(parsed.expiresAt);
    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) return null;
    return { accessToken: parsed.accessToken, expiresAt: parsed.expiresAt, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

function readFilename(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  return header.match(/filename="([^"]+)"/i)?.[1] ?? null;
}

function readPortalDownloadIndex(href: string | null | undefined): number | null {
  // Only relative URLs are owned by the Shoppex customer portal. An external
  // site may legitimately use the same pathname and must remain an external link.
  if (!href?.startsWith('/api/customer/')) return null;
  try {
    const url = new URL(href, 'https://headless-customer.invalid');
    if (!/^\/api\/customer\/invoice\/[^/]+\/download$/.test(url.pathname)) return null;
    const rawIndex = url.searchParams.get('file_index');
    if (rawIndex === null || !/^\d+$/.test(rawIndex)) return null;
    const index = Number(rawIndex);
    return Number.isSafeInteger(index) ? index : null;
  } catch {
    return null;
  }
}

function readBrowserStorage(name: 'sessionStorage' | 'localStorage'):
  Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  try {
    return typeof window === 'undefined' ? null : window[name];
  } catch {
    return null;
  }
}

export class HeadlessCustomerClient {
  private readonly base: string;
  private readonly shop: string;
  private readonly publishableKey: string;
  private readonly fetchImpl: typeof fetch;
  private readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  private readonly persistentStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  private readonly storageKey: string;
  private memorySession: StoredSession | null = null;

  constructor(config: HeadlessCustomerClientConfig) {
    if (!config.shop.trim()) throw new Error('HeadlessCustomerClient requires a shop.');
    if (!config.publishableKey.trim()) {
      throw new Error('HeadlessCustomerClient requires a publishableKey.');
    }
    this.shop = config.shop.trim();
    this.publishableKey = config.publishableKey.trim();
    this.base = (config.apiBaseUrl ?? DEFAULT_API_ORIGIN).replace(/\/+$/, '');
    this.fetchImpl = config.fetchImpl ?? globalThis.fetch?.bind(globalThis);
    if (!this.fetchImpl) throw new Error('No fetch implementation available; pass fetchImpl.');
    this.storage = config.storage ?? readBrowserStorage('sessionStorage');
    this.persistentStorage = config.persistentStorage ?? readBrowserStorage('localStorage');
    this.storageKey = `${SESSION_STORAGE_PREFIX}${this.shop}`;
  }

  getSession(): HeadlessCustomerSession | null {
    const candidates: StoredSession[] = this.memorySession ? [this.memorySession] : [];
    if (this.storage) {
      try {
        const stored = parseStoredSession(this.storage.getItem(this.storageKey));
        if (stored) candidates.push(stored);
      } catch {
        // Continue with memory and persistent storage.
      }
    }
    if (this.persistentStorage) {
      try {
        const stored = parseStoredSession(this.persistentStorage.getItem(this.storageKey));
        if (stored) candidates.push(stored);
      } catch {
        // Continue with the other available stores.
      }
    }

    const stored = candidates.sort((left, right) => right.savedAt - left.savedAt)[0] ?? null;

    if (!stored) {
      this.clearSession();
      return null;
    }
    this.memorySession = stored;
    return { accessToken: stored.accessToken, expiresAt: stored.expiresAt };
  }

  clearSession(): void {
    this.memorySession = null;
    try {
      this.storage?.removeItem(this.storageKey);
    } catch {
      // Privacy-restricted browsers may reject storage access. Memory is still cleared.
    }
    try {
      this.persistentStorage?.removeItem(this.storageKey);
    } catch {
      // The two stores are cleared independently so one rejected access cannot block the other.
    }
  }

  private saveSession(session: HeadlessCustomerSession, rememberMe: boolean): void {
    const storedSession: StoredSession = { ...session, savedAt: Date.now() };
    this.memorySession = storedSession;
    const target = rememberMe ? this.persistentStorage : this.storage;
    const other = rememberMe ? this.storage : this.persistentStorage;
    try {
      target?.setItem(this.storageKey, JSON.stringify(storedSession));
    } catch {
      // Keep the session for this client instance when browser storage is unavailable.
    }
    try {
      other?.removeItem(this.storageKey);
    } catch {
      // A stale session in the other storage cannot override the in-memory session.
    }
  }

  private url(path: string, query: Record<string, string | undefined> = {}): string {
    const url = new URL(`${this.base}/v1/customer${path}`);
    url.searchParams.set('shop', this.shop);
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, value);
    }
    return url.toString();
  }

  private async request<T>(
    path: string,
    options: {
      method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
      body?: unknown;
      formData?: FormData;
      authenticated?: boolean;
      schema?: CustomerPayloadSchema<T>;
      query?: Record<string, string | undefined>;
    } = {},
  ): Promise<T> {
    if (options.body !== undefined && options.formData !== undefined) {
      throw new Error('Customer requests cannot send JSON and FormData together.');
    }
    const session = options.authenticated ? this.getSession() : null;
    if (options.authenticated && !session) {
      throw new HeadlessCustomerError(401, 'Customer session is missing or expired.', 'UNAUTHORIZED');
    }

    const headers: Record<string, string> = {
      accept: 'application/json',
      'x-shoppex-publishable-key': this.publishableKey,
      ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(session ? { authorization: `Bearer ${session.accessToken}` } : {}),
    };
    const requestBody = options.formData ?? (
      options.body === undefined ? undefined : JSON.stringify(options.body)
    );
    const response = await this.fetchImpl(this.url(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      ...(requestBody === undefined ? {} : { body: requestBody }),
      cache: 'no-store',
    });

    let envelope: ApiEnvelope<unknown> | null = null;
    try {
      envelope = await response.json() as ApiEnvelope<unknown>;
    } catch {
      // Report a stable transport error below.
    }
    if (!response.ok || !envelope || envelope.error) {
      if (response.status === 401 && options.authenticated) this.clearSession();
      throw new HeadlessCustomerError(
        response.status,
        envelope?.error ?? `Request failed (${response.status})`,
        envelope?.error_code ?? null,
      );
    }

    if (!options.schema) return envelope.data as T;
    const parsed = options.schema.safeParse(envelope.data);
    if (!parsed.success) {
      throw new HeadlessCustomerError(response.status, 'Customer API response did not match the expected contract.');
    }
    return parsed.data;
  }

  private async requestFile(
    path: string,
    query: Record<string, string | undefined> = {},
    options: { rejectHtml?: boolean } = {},
  ): Promise<HeadlessCustomerDownload> {
    const session = this.getSession();
    if (!session) {
      throw new HeadlessCustomerError(401, 'Customer session is missing or expired.', 'UNAUTHORIZED');
    }

    const response = await this.fetchImpl(this.url(path, query), {
      method: 'GET',
      headers: {
        'x-shoppex-publishable-key': this.publishableKey,
        authorization: `Bearer ${session.accessToken}`,
      },
      cache: 'no-store',
    });
    if (!response.ok) {
      if (response.status === 401) this.clearSession();
      let message = `Download failed (${response.status})`;
      try {
        const body = await response.json() as ApiEnvelope<unknown>;
        if (body.error) message = body.error;
      } catch {
        // Keep the stable status-based error.
      }
      throw new HeadlessCustomerError(response.status, message);
    }

    const contentType = response.headers.get('content-type');
    if (options.rejectHtml && contentType?.toLowerCase().startsWith('text/html')) {
      throw new HeadlessCustomerError(
        502,
        'Shoppex returned an unsafe aggregate download response.',
        'UNSAFE_DOWNLOAD_RESPONSE',
      );
    }

    return {
      blob: await response.blob(),
      filename: readFilename(response.headers.get('content-disposition')),
      contentType,
    };
  }

  requestOtp(email: string): Promise<{ requested: boolean }> {
    return this.request('/auth/otp/request', {
      method: 'POST',
      body: { shop: this.shop, email },
    });
  }

  async verifyOtp(
    email: string,
    otp: string,
    options: HeadlessCustomerVerifyOptions = {},
  ): Promise<HeadlessCustomerSession> {
    const data = await this.request<{ access_token: string; expires_at: string }>('/auth/otp/verify', {
      method: 'POST',
      body: { shop: this.shop, email, otp, remember_me: options.rememberMe ?? false },
    });
    const expiresAtMs = typeof data?.expires_at === 'string' ? Date.parse(data.expires_at) : Number.NaN;
    if (
      typeof data?.access_token !== 'string'
      || !data.access_token.startsWith('hcs_')
      || typeof data.expires_at !== 'string'
      || !Number.isFinite(expiresAtMs)
      || expiresAtMs <= Date.now()
    ) {
      throw new HeadlessCustomerError(200, 'Customer API response did not match the expected contract.');
    }
    const session = { accessToken: data.access_token, expiresAt: data.expires_at };
    this.saveSession(session, options.rememberMe ?? false);
    return session;
  }

  async me(): Promise<HeadlessCustomerMe> {
    const data = await this.request<HeadlessCustomerMeWire>('/me', {
      authenticated: true,
      schema: customerPortalMeSchema,
    });
    if (data.token !== null) {
      // A raw BetterAuth token here would bypass the external route allowlist.
      // Reject the response instead of handing that credential to merchant JS.
      throw new HeadlessCustomerError(200, 'Customer API response did not match the expected contract.');
    }
    return { customer: data.customer, shop: data.shop };
  }

  async dashboard(): Promise<HeadlessCustomerDashboard> {
    const dashboard = await this.request<CustomerPortalDashboardWire>('/dashboard', {
      authenticated: true,
      schema: customerPortalDashboardSchema,
    });
    return {
      ...dashboard,
      deliverables: dashboard.deliverables.map((deliverable) => {
        const downloadIndex = readPortalDownloadIndex(deliverable.href);
        return downloadIndex === null
          ? deliverable
          : { ...deliverable, href: null, downloadIndex };
      }),
    };
  }

  updateProfile(input: { name: string }): Promise<HeadlessCustomerProfileUpdate> {
    return this.request('/profile', {
      method: 'POST',
      authenticated: true,
      body: { name: input.name },
      schema: customerPortalProfileUpdateSchema,
    });
  }

  updateAvatar(file: File): Promise<HeadlessCustomerAvatar> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request('/profile/avatar', {
      method: 'POST',
      authenticated: true,
      formData,
      schema: customerPortalAvatarSchema,
    });
  }

  removeAvatar(): Promise<HeadlessCustomerAvatar> {
    return this.request('/profile/avatar', {
      method: 'DELETE',
      authenticated: true,
      schema: customerPortalAvatarSchema,
    });
  }

  emailPreferences(): Promise<HeadlessCustomerEmailPreferences> {
    return this.request('/email-marketing/preferences', {
      authenticated: true,
      schema: customerPortalEmailPreferencesSchema,
    });
  }

  updateEmailPreferences(
    patch: HeadlessCustomerEmailPreferencesPatch,
  ): Promise<HeadlessCustomerEmailPreferencesUpdate> {
    return this.request('/email-marketing/preferences', {
      method: 'PATCH',
      authenticated: true,
      body: {
        ...(patch.globalUnsubscribed === undefined
          ? {}
          : { global_unsubscribed: patch.globalUnsubscribed }),
        list_subscriptions: (patch.listSubscriptions ?? []).map((item) => ({
          list_id: item.listId,
          subscribed: item.subscribed,
        })),
      },
      schema: customerPortalEmailPreferencesUpdateSchema,
    });
  }

  notificationPreferences(): Promise<HeadlessCustomerNotificationPreferences> {
    return this.request('/notification-preferences', {
      authenticated: true,
      schema: customerPortalNotificationPreferencesSchema,
    });
  }

  updateNotificationPreferences(
    patch: HeadlessCustomerNotificationPreferencesPatch,
  ): Promise<HeadlessCustomerNotificationPreferences> {
    return this.request('/notification-preferences', {
      method: 'PATCH',
      authenticated: true,
      body: {
        ...(patch.orderCreatedEmails === undefined
          ? {}
          : { order_created_emails: patch.orderCreatedEmails }),
        ...(patch.orderCompletedEmails === undefined
          ? {}
          : { order_completed_emails: patch.orderCompletedEmails }),
        ...(patch.paymentIssueEmails === undefined
          ? {}
          : { payment_issue_emails: patch.paymentIssueEmails }),
      },
      schema: customerPortalNotificationPreferencesSchema,
    });
  }

  orders(options: HeadlessCustomerOrdersQuery = {}): Promise<HeadlessCustomerOrders> {
    return this.request<HeadlessCustomerOrders>('/invoices', {
      authenticated: true,
      schema: customerPortalPaginatedInvoicesSchema,
      query: {
        page: options.page === undefined ? undefined : String(options.page),
        limit: options.limit === undefined ? undefined : String(options.limit),
        status: options.status,
        q: options.search,
      },
    });
  }

  async order(id: string): Promise<HeadlessCustomerOrder> {
    const order = await this.request<HeadlessCustomerOrder>(`/invoice/${encodeURIComponent(id)}`, {
      authenticated: true,
      schema: customerPortalInvoiceDetailSchema,
    });
    return {
      ...order,
      lineItems: order.lineItems.map((lineItem) => ({
        ...lineItem,
        deliverySummary: lineItem.deliverySummary
          ? {
              ...lineItem.deliverySummary,
              links: lineItem.deliverySummary.links.map((link) => {
                const downloadIndex = readPortalDownloadIndex(link.href);
                return downloadIndex === null
                  ? link
                  : { ...link, href: null, downloadIndex };
              }),
            }
          : null,
      })),
    };
  }

  orderReview(orderId: string): Promise<HeadlessCustomerOrderReview> {
    return this.request(`/invoices/${encodeURIComponent(orderId)}/review`, {
      authenticated: true,
      schema: customerPortalOrderReviewSchema,
    });
  }

  submitOrderReview(
    orderId: string,
    input: HeadlessCustomerOrderReviewInput,
  ): Promise<HeadlessCustomerOrderReview> {
    return this.request(`/invoices/${encodeURIComponent(orderId)}/review`, {
      method: 'POST',
      authenticated: true,
      body: {
        score: input.score,
        message: input.message,
      },
      schema: customerPortalOrderReviewSchema,
    });
  }

  invoicePdf(orderId: string): Promise<HeadlessCustomerDownload> {
    return this.requestFile(`/invoices/${encodeURIComponent(orderId)}/pdf`);
  }

  replacementEligibility(
    orderId: string,
    lineItemId?: string,
  ): Promise<HeadlessCustomerReplacementEligibility> {
    return this.request(`/invoices/${encodeURIComponent(orderId)}/replacement-eligibility`, {
      authenticated: true,
      query: { line_item_id: lineItemId },
      schema: customerReplacementEligibilitySchema,
    });
  }

  requestReplacement(
    orderId: string,
    options: HeadlessCustomerReplacementOptions,
  ): Promise<HeadlessCustomerReplacementRequest> {
    return this.request(`/invoices/${encodeURIComponent(orderId)}/replacement-requests`, {
      method: 'POST',
      authenticated: true,
      body: {
        line_item_id: options.lineItemId,
        reason: options.reason,
        ...(options.idempotencyKey ? { idempotency_key: options.idempotencyKey } : {}),
      },
      schema: customerReplacementRequestSchema,
    });
  }

  async download(orderId: string, fileIndex: number): Promise<HeadlessCustomerDownload> {
    if (!Number.isSafeInteger(fileIndex) || fileIndex < 0) {
      throw new HeadlessCustomerError(
        400,
        'download requires a non-negative integer fileIndex from order().deliverySummary.links.',
        'INVALID_FILE_INDEX',
      );
    }
    return this.requestFile(
      `/invoice/${encodeURIComponent(orderId)}/download`,
      { file_index: String(fileIndex) },
      { rejectHtml: true },
    );
  }

  resetLicenseHwid(licenseId: string): Promise<HeadlessCustomerLicenseReset> {
    return this.request(`/licenses/${encodeURIComponent(licenseId)}/reset-hwid`, {
      method: 'POST',
      authenticated: true,
      body: {},
      schema: customerPortalLicenseResetSchema,
    });
  }

  renewLicense(licenseId: string): Promise<HeadlessCustomerLicenseRenewal> {
    return this.request(`/licenses/${encodeURIComponent(licenseId)}/renew`, {
      method: 'POST',
      authenticated: true,
      // This established route requires shop in the body. The shared headless
      // gate independently verifies the same shop from the query and key.
      body: { shop: this.shop },
      schema: customerPortalLicenseRenewalSchema,
    });
  }

  walletTopupQuote(): Promise<HeadlessCustomerWalletTopupQuote> {
    return this.request('/wallet/topup-quote', {
      authenticated: true,
      schema: customerPortalWalletTopupQuoteSchema,
    });
  }

  createWalletTopup(amount: string): Promise<HeadlessCustomerWalletTopup> {
    return this.request('/wallet/topup', {
      method: 'POST',
      authenticated: true,
      body: { amount },
      schema: customerPortalWalletTopupResultSchema,
    });
  }

  subscriptionBillingHistory(
    subscriptionId: string,
  ): Promise<HeadlessCustomerSubscriptionBillingHistory> {
    return this.request(`/subscriptions/${encodeURIComponent(subscriptionId)}/billing-history`, {
      authenticated: true,
      schema: customerPortalSubscriptionBillingHistorySchema,
    });
  }

  cancelSubscription(
    subscriptionId: string,
    options: HeadlessCustomerSubscriptionCancelOptions = {},
  ): Promise<HeadlessCustomerSubscriptionCancel> {
    return this.request(`/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`, {
      method: 'POST',
      authenticated: true,
      body: {
        ...(options.cancelAtPeriodEnd === undefined
          ? {}
          : { cancel_at_period_end: options.cancelAtPeriodEnd }),
        ...(options.reason === undefined ? {} : { reason: options.reason }),
      },
      schema: customerPortalSubscriptionCancelSchema,
    });
  }

  pauseSubscription(subscriptionId: string): Promise<HeadlessCustomerSubscriptionStatus> {
    return this.request(`/subscriptions/${encodeURIComponent(subscriptionId)}/pause`, {
      method: 'POST',
      authenticated: true,
      body: {},
      schema: customerPortalSubscriptionStatusSchema,
    });
  }

  resumeSubscription(subscriptionId: string): Promise<HeadlessCustomerSubscriptionStatus> {
    return this.request(`/subscriptions/${encodeURIComponent(subscriptionId)}/resume`, {
      method: 'POST',
      authenticated: true,
      body: {},
      schema: customerPortalSubscriptionStatusSchema,
    });
  }

  loyalty(): Promise<HeadlessCustomerLoyalty> {
    return this.request('/loyalty', {
      authenticated: true,
      schema: customerLoyaltySchema,
    });
  }

  redeemLoyaltyPoints(input: {
    points: number;
    idempotencyKey: string;
  }): Promise<HeadlessCustomerLoyaltyRedeem> {
    return this.request('/loyalty/redeem', {
      method: 'POST',
      authenticated: true,
      body: { points: input.points, idempotency_key: input.idempotencyKey },
      schema: customerLoyaltyRedeemSchema,
    });
  }

  warranties(invoiceId?: string): Promise<HeadlessCustomerWarranties> {
    return this.request('/warranties', {
      authenticated: true,
      query: { invoice: invoiceId },
      schema: customerWarrantyListSchema,
    });
  }

  claimWarranty(warrantyId: string, message?: string): Promise<HeadlessCustomerWarrantyClaim> {
    return this.request(`/warranties/${encodeURIComponent(warrantyId)}/claim`, {
      method: 'POST',
      authenticated: true,
      body: message ? { message } : {},
      schema: customerWarrantyClaimSchema,
    });
  }

  favorites(): Promise<HeadlessCustomerFavorites> {
    return this.request('/favorites', {
      authenticated: true,
      schema: customerPortalFavoritesSchema,
    });
  }

  addFavorite(productId: string): Promise<HeadlessCustomerFavoriteMutation> {
    return this.request(`/favorites/${encodeURIComponent(productId)}`, {
      method: 'PUT',
      authenticated: true,
      schema: customerPortalFavoriteMutationSchema,
    });
  }

  removeFavorite(productId: string): Promise<HeadlessCustomerFavoriteMutation> {
    return this.request(`/favorites/${encodeURIComponent(productId)}`, {
      method: 'DELETE',
      authenticated: true,
      schema: customerPortalFavoriteMutationSchema,
    });
  }

  affiliate(): Promise<HeadlessCustomerAffiliate> {
    return this.request('/affiliate', {
      authenticated: true,
      schema: customerPortalAffiliateSummarySchema,
    });
  }

  affiliateStats(input?: { days?: number }): Promise<HeadlessCustomerAffiliateStats> {
    return this.request('/affiliate/stats', {
      authenticated: true,
      schema: customerPortalAffiliateStatsSchema,
      query: input?.days === undefined ? {} : { days: String(input.days) },
    });
  }

  createAffiliateLink(
    input: HeadlessCustomerAffiliateLinkInput,
  ): Promise<HeadlessCustomerAffiliateLink> {
    return this.request('/affiliate/links', {
      method: 'POST',
      authenticated: true,
      body: {
        shop: this.shop,
        code: input.code,
        label: input.label,
        ...(input.requestedRevenuePercent === undefined
          ? {}
          : { requested_affiliate_revenue_percent: input.requestedRevenuePercent }),
        ...(input.requestedCustomerDiscountPercent === undefined
          ? {}
          : { requested_affiliate_customer_discount_percent: input.requestedCustomerDiscountPercent }),
      },
      schema: customerPortalAffiliateCreateLinkSchema,
    });
  }

  createTicket(input: HeadlessCustomerTicketInput): Promise<HeadlessCustomerTicketCreateResult> {
    return this.request('/tickets', {
      method: 'POST',
      authenticated: true,
      body: {
        ...(input.title === undefined ? {} : { title: input.title }),
        message: input.message,
        ...(input.invoiceId === undefined ? {} : { invoice_id: input.invoiceId }),
      },
      schema: customerPortalSupportCreateSchema,
    });
  }

  ticket(ticketId: string): Promise<HeadlessCustomerTicket> {
    return this.request(`/tickets/${encodeURIComponent(ticketId)}`, {
      authenticated: true,
      schema: customerPortalSupportTicketDetailSchema,
    });
  }

  async replyToTicket(ticketId: string, message: string): Promise<void> {
    await this.request<null>(`/tickets/${encodeURIComponent(ticketId)}/reply`, {
      method: 'POST',
      authenticated: true,
      body: { message },
    });
  }

  reseller(): Promise<HeadlessCustomerReseller> {
    return this.request('/reseller', {
      authenticated: true,
      schema: customerResellerSummarySchema,
    });
  }

  applyForReseller(note?: string): Promise<HeadlessCustomerResellerEnrollment> {
    return this.request('/reseller/apply', {
      method: 'POST',
      authenticated: true,
      body: note === undefined ? {} : { note },
      schema: customerResellerEnrollmentSchema,
    });
  }

  enrollAsReseller(): Promise<HeadlessCustomerResellerEnrollment> {
    return this.request('/reseller/enroll', {
      method: 'POST',
      authenticated: true,
      body: {},
      schema: customerResellerEnrollmentSchema,
    });
  }

  acceptResellerInvite(token: string): Promise<HeadlessCustomerResellerEnrollment> {
    return this.request('/reseller/accept-invite', {
      method: 'POST',
      authenticated: true,
      body: { token },
      schema: customerResellerEnrollmentSchema,
    });
  }

  resellerCatalog(
    options: HeadlessCustomerResellerCatalogQuery = {},
  ): Promise<HeadlessCustomerResellerCatalog> {
    return this.request('/reseller/catalog', {
      authenticated: true,
      query: {
        search: options.search,
        page: options.page === undefined ? undefined : String(options.page),
        per_page: options.perPage === undefined ? undefined : String(options.perPage),
      },
      schema: customerResellerCatalogSchema,
    });
  }

  quoteResellerOrder(
    items: HeadlessCustomerResellerOrderItem[],
  ): Promise<HeadlessCustomerResellerQuote> {
    return this.request('/reseller/orders/quote', {
      method: 'POST',
      authenticated: true,
      body: {
        items: items.map((item) => ({
          product_id: item.productId,
          ...(item.variantId === undefined ? {} : { variant_id: item.variantId }),
          quantity: item.quantity,
        })),
      },
      schema: customerResellerQuoteSchema,
    });
  }

  resellerOrders(
    options: HeadlessCustomerResellerOrdersQuery = {},
  ): Promise<HeadlessCustomerResellerOrders> {
    return this.request('/reseller/orders', {
      authenticated: true,
      query: {
        page: options.page === undefined ? undefined : String(options.page),
        per_page: options.perPage === undefined ? undefined : String(options.perPage),
      },
      schema: customerResellerOrdersSchema,
    });
  }

  resellerOrder(orderId: string): Promise<HeadlessCustomerResellerOrder> {
    return this.request(`/reseller/orders/${encodeURIComponent(orderId)}`, {
      authenticated: true,
      schema: customerResellerOrderSchema,
    });
  }

  resellerWallet(): Promise<HeadlessCustomerResellerWallet> {
    return this.request('/reseller/wallet', {
      authenticated: true,
      schema: customerResellerWalletSchema,
    });
  }

  resellerTopupQuote(): Promise<HeadlessCustomerWalletTopupQuote> {
    return this.request('/reseller/wallet/topup-quote', {
      authenticated: true,
      schema: customerPortalWalletTopupQuoteSchema,
    });
  }

  createResellerTopup(amount: string): Promise<HeadlessCustomerResellerTopup> {
    return this.request('/reseller/wallet/topup', {
      method: 'POST',
      authenticated: true,
      body: { amount },
      schema: customerResellerTopupSchema,
    });
  }

  resellerApiKeys(): Promise<HeadlessCustomerResellerApiKeys> {
    return this.request('/reseller/api-keys', {
      authenticated: true,
      schema: customerResellerApiKeysSchema,
    });
  }

  async logout(): Promise<void> {
    if (!this.getSession()) return;
    try {
      await this.request<null>('/auth/logout', { method: 'POST', authenticated: true });
    } catch (error) {
      if (error instanceof HeadlessCustomerError && error.status === 401) {
        // The server already considers the credential invalid. request() also
        // clears it, so logout is complete from the buyer's perspective.
        return;
      }
      // Keep the local token after a transient failure so the caller can retry
      // server-side revocation instead of leaving an unreachable live session.
      throw error;
    }
    this.clearSession();
  }
}

export function createCustomerClient(config: HeadlessCustomerClientConfig): HeadlessCustomerClient {
  return new HeadlessCustomerClient(config);
}
